import { useMutation, useQuery } from '@tanstack/react-query';
import { QueryParams } from '@/model';
import { useCallback, useMemo } from 'react';
import { ChallengeService } from '../instances/challenge';
import { ChallengeConverter } from '@/converters/challenge';

export const useGetChallenge = (params?: QueryParams) => {
  const query = useQuery(
    ['GET_CHALLENGE', params],
    async () => {
      const [err, data] = await ChallengeService.getChallenge(params);
      if (err) {
        throw err;
      }
      return {
        data: ChallengeConverter.toDomainFromChallengesPaginatedResponse(data),
        meta: data.meta,
      };
    },
    {
      refetchOnWindowFocus: false,
    },
  );

  const forceRefetch = useCallback(() => {
    query.remove();
    query.refetch();
  }, [query]);

  const returnQuery = useMemo(() => ({ ...query, forceRefetch }), [forceRefetch, query]);
  return returnQuery;
};

export const usePutChallengeMutation = () => {
  return useMutation({
    mutationKey: ['PUT_CHALLENGE'],
    mutationFn: async ({ id, payload }: any) => {
      const [err, data] = await ChallengeService.putChallenge(id, payload);
      if (err) {
        throw err;
      }
      return ChallengeConverter.toDomainFromPutChallengeResponse(data);
    },
  });
};

export const usePostChallengeMutation = () => {
  return useMutation({
    mutationKey: ['POST_CHALLENGE'],
    mutationFn: async (payload: any) => {
      const [err, data] = await ChallengeService.postChallenge(payload);
      if (err) {
        throw err;
      }
      return ChallengeConverter.toDomainFromPostChallengeResponse(data);
    },
  });
};

export const useDeleteChallengeMutation = () => {
  return useMutation({
    mutationKey: ['DELETE_CHALLENGE'],
    mutationFn: async (id: string) => {
      const [err] = await ChallengeService.deleteChallenge(id);
      if (err) {
        throw err;
      }
    },
  });
};

export const usePutChallengeCloseMutation = () => {
  return useMutation({
    mutationKey: ['PUT_CHALLENGE_CLOSE'],
    mutationFn: async (id: string) => {
      const [err] = await ChallengeService.putChallengeClose(id);
      if (err) {
        throw err;
      }
    },
  });
};

export const usePutChallengePublishedMutation = () => {
  return useMutation({
    mutationKey: ['PUT_CHALLENGE_PUBLISHED'],
    mutationFn: async (id: string) => {
      const [err] = await ChallengeService.putChallengePublished(id);
      if (err) {
        throw err;
      }
    },
  });
};

export const usePutChallengeUnpublishedMutation = () => {
  return useMutation({
    mutationKey: ['PUT_CHALLENGE_UNPUBLISHED'],
    mutationFn: async (id: string) => {
      const [err] = await ChallengeService.putChallengeUnpublished(id);
      if (err) {
        throw err;
      }
    },
  });
};

export const useGetChallengeMetricsMutation = () => {
  return useQuery({
    queryKey: ['GET_CHALLENGE_METRICS'],
    queryFn: async () => {
      const [err, data] = await ChallengeService.getChallengeMetrics();
      if (err) {
        throw err;
      }
      return ChallengeConverter.toDomainFromGetChallengeMetricsResponse(data);
    },
  });
};

export const useGetChallengeByUUID = (uuid: string, enabled = true) => {
  return useQuery({
    queryKey: ['GET_CHALLENGE_BY_ID', uuid],
    queryFn: async () => {
      const [err, data] = await ChallengeService.getChallengeByUUID(uuid);
      if (err) {
        throw err;
      }
      return ChallengeConverter.toDomainFromGetChallengeByUUIDResponse(data);
    },
    enabled,
    refetchOnWindowFocus: false,
  });
};
